import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { useCart } from "../context/CartContext";
import { AiFillStar, AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { FiShoppingCart } from "react-icons/fi";

const ProductDetail = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);

  const [currentImage, setCurrentImage] = useState(0);
  const [wishlist, setWishlist] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const { data } = await axios.get(`/api/products/${id}`);
      setProduct(data);
      setSelectedSize(data.sizes?.[0] || "");
    } catch (err) {
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {

    if (!selectedSize) {
      toast.error("Select a size");
      return;
    }

    addToCart({
      ...product,
      quantity,
      selectedSize,
    });

    toast.success("Added to cart");
  };

  const handleBuyNow = () => {

    handleAddToCart();
    navigate("/checkout");
  };

  if (loading || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-20 w-20 border-b-2 border-yellow-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-gray-50 dark:bg-black">

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14">

        {/* IMAGE SECTION */}

        <div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-2xl"
          >

            <img
              src={product.images[currentImage]}
              alt={product.name}
              className="w-full h-[650px] object-cover hover:scale-105 transition duration-500"
            />

          </motion.div>

          {/* THUMBNAILS */}

          <div className="flex gap-3 mt-6">

            {product.images.map((img, index) => (

              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`w-24 h-24 rounded-xl overflow-hidden border-2 ${
                  currentImage === index
                    ? "border-yellow-500"
                    : "border-gray-200"
                }`}
              >

                <img
                  src={img}
                  alt=""
                  className="w-full h-full object-cover"
                />

              </button>

            ))}

          </div>

        </div>

        {/* PRODUCT INFO */}

        <div className="lg:sticky top-28 h-fit">

          <div className="bg-white dark:bg-gray-900 p-10 rounded-3xl shadow-2xl">

            {/* TITLE */}

            <h1 className="text-4xl font-bold mb-4">
              {product.name}
            </h1>

            {/* RATING */}

            <div className="flex items-center mb-6">

              <div className="flex text-yellow-500 mr-3">

                {[...Array(5)].map((_, i) => (
                  <AiFillStar key={i} />
                ))}

              </div>

              <span className="text-gray-500">(128 reviews)</span>

            </div>

            {/* PRICE */}

            <div className="flex items-center gap-4 mb-8">

              <span className="text-4xl font-bold">
                ₹{product.price.toLocaleString()}
              </span>

              {product.originalPrice && (

                <span className="line-through text-gray-400 text-xl">
                  ₹{product.originalPrice.toLocaleString()}
                </span>

              )}

            </div>

            {/* SIZE */}

            <div className="mb-8">

              <h3 className="font-semibold mb-3">
                Select Size
              </h3>

              <div className="flex flex-wrap gap-3">

                {product.sizes.map((size) => (

                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`px-5 py-3 border rounded-xl ${
                      selectedSize === size
                        ? "bg-yellow-500 text-black border-yellow-500"
                        : "border-gray-300"
                    }`}
                  >

                    {size}

                  </button>

                ))}

              </div>

            </div>

            {/* QUANTITY */}

            <div className="flex items-center gap-4 mb-10">

              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-4 py-2 border rounded-lg"
              >
                -
              </button>

              <span className="text-xl font-bold">
                {quantity}
              </span>

              <button
                onClick={() => setQuantity(quantity + 1)}
                className="px-4 py-2 border rounded-lg"
              >
                +
              </button>

            </div>

            {/* BUTTONS */}

            <div className="flex gap-4 mb-8">

              <motion.button
                onClick={handleAddToCart}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black py-4 rounded-xl font-semibold flex items-center justify-center gap-2"
                whileTap={{ scale: 0.95 }}
              >

                <FiShoppingCart />
                Add to Cart

              </motion.button>

              <motion.button
                onClick={handleBuyNow}
                className="flex-1 bg-black text-white py-4 rounded-xl font-semibold"
                whileTap={{ scale: 0.95 }}
              >

                Buy Now

              </motion.button>

            </div>

            {/* WISHLIST */}

            <button
              onClick={() => setWishlist(!wishlist)}
              className="flex items-center gap-2 text-lg"
            >

              {wishlist ? (
                <AiFillHeart className="text-red-500" />
              ) : (
                <AiOutlineHeart />
              )}

              Wishlist

            </button>

            {/* DESCRIPTION */}

            <div className="mt-10 border-t pt-8">

              <h3 className="text-xl font-semibold mb-4">
                Product Details
              </h3>

              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default ProductDetail;